import type { EmployerSnapshot, Session } from '@klukka/client';
import { periodInfo, type AdminLang } from '@klukka/core';
import { ErrorBanner, Ic, Spinner, useApiResource } from '@klukka/ui';
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { calcAll } from './derive';
import { Login } from './Login';
import { AnalyticsTab } from './tabs/AnalyticsTab';
import { ApprovalsTab } from './tabs/ApprovalsTab';
import { DashboardTab } from './tabs/DashboardTab';
import { EmployeesTab } from './tabs/EmployeesTab';
import { ImporterTab } from './tabs/ImporterTab';
import { PayrollTab } from './tabs/PayrollTab';
import { ReportsTab } from './tabs/ReportsTab';
import { TimesheetsTab } from './tabs/TimesheetsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { makeTr, type Tr } from './widgets';

type Nav = 'dashboard' | 'analytics' | 'employees' | 'settings' | 'approvals' | 'importer' | 'timesheets' | 'reports' | 'payroll';

const LANG_KEY = 'klukka.admin.lang';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [lang, setLang] = useState<AdminLang>(
    () => (window.localStorage.getItem(LANG_KEY) as AdminLang) || 'is',
  );

  useEffect(() => {
    void api.currentSession().then((s) => {
      setSession(s);
      setChecking(false);
    });
  }, []);

  const changeLang = useCallback((l: AdminLang) => {
    window.localStorage.setItem(LANG_KEY, l);
    setLang(l);
  }, []);

  if (checking) return <Spinner label="…" />;
  if (!session) return <Login lang={lang} onLang={changeLang} onSignedIn={setSession} />;

  return (
    <Workspace
      key={session.token}
      session={session}
      lang={lang}
      onLang={changeLang}
      onSignedOut={() => setSession(null)}
    />
  );
}

function Workspace({ session, lang, onLang, onSignedOut }: {
  session: Session;
  lang: AdminLang;
  onLang: (l: AdminLang) => void;
  onSignedOut: () => void;
}) {
  const load = useCallback(() => api.getEmployerSnapshot(), []);
  const res = useApiResource<EmployerSnapshot>(load);
  const [nav, setNav] = useState<Nav>('dashboard');
  const [offset, setOffset] = useState(0);
  const tr = makeTr(lang);

  const snap = res.data;

  // Live clocks keep the dashboard honest without the user reloading.
  useEffect(() => {
    if (nav !== 'dashboard') return;
    const id = window.setInterval(() => void res.reload(), 30_000);
    return () => window.clearInterval(id);
  }, [nav, res]);

  if (res.loading && !snap) return <Spinner label={tr('loading')} />;

  if (!snap) {
    return (
      <div style={{ padding: 28, maxWidth: 520 }}>
        <ErrorBanner message={tr('saveFailed')} onRetry={() => void res.reload()} retryLabel={tr('retry')} />
      </div>
    );
  }

  const pinfo = periodInfo(snap.company.settings, snap.serverToday, offset);
  const all = calcAll(snap, offset);
  const pending =
    snap.requests.filter((r) => r.status === 'pending').length +
    snap.timesheets.filter((t) => t.status === 'submitted').length;

  const run = (fn: () => Promise<EmployerSnapshot>) => { void res.mutate(fn).catch(() => undefined); };

  const items: Array<[Nav, (p: { size?: number }) => JSX.Element, number?]> = [
    ['dashboard', Ic.chart],
    ['analytics', Ic.chart],
    ['employees', Ic.user],
    ['settings', Ic.settings],
    ['approvals', Ic.inbox, pending],
    ['importer', Ic.download],
    ['timesheets', Ic.doc],
    ['reports', Ic.doc],
    ['payroll', Ic.sign],
  ];

  return (
    <div className="desk">
      <aside className="side">
        <div className="side-co">
          <b>{snap.company.name}</b>
          <span>{snap.company.kennitala}</span>
        </div>

        {items.map(([key, Icon, badge]) => (
          <button key={key} className={nav === key ? 'on' : ''} onClick={() => setNav(key)}>
            <span className="side-lbl"><Icon size={15} /> {tr(key)}</span>
            {badge ? <span className="badge">{badge}</span> : null}
          </button>
        ))}

        <div className="side-foot">
          <div className="side-user">
            <b>{session.name}</b>
            {tr('admin')}
          </div>
          <div className="seg3">
            <button className={lang === 'is' ? 'on' : ''} onClick={() => onLang('is')}>IS</button>
            <button className={lang === 'en' ? 'on' : ''} onClick={() => onLang('en')}>EN</button>
          </div>
          <button className="btn-sm" onClick={() => void api.logout().then(onSignedOut)}>
            <Ic.logout size={14} /> {tr('logout')}
          </button>
        </div>
      </aside>

      <div className="main">
        <div className="main-head">
          <h2>{tr(nav)}</h2>
          <span className="period-nav">
            <button onClick={() => setOffset(offset - 1)} aria-label="Previous period">‹</button>
            <b>{pinfo.start} → {pinfo.end}</b>
            <button disabled={offset >= 0} onClick={() => setOffset(offset + 1)} aria-label="Next period">›</button>
            {offset !== 0 ? <button className="now" onClick={() => setOffset(0)}>{tr('current')}</button> : null}
          </span>
          {res.busy ? <span className="dimtx">…</span> : null}
        </div>

        {res.error ? (
          <div style={{ marginBottom: 16 }}>
            <ErrorBanner
              message={`${tr('saveFailed')} — ${res.error.message}`}
              onRetry={() => { res.clearError(); void res.reload(); }}
              retryLabel={tr('retry')}
            />
          </div>
        ) : null}

        {nav === 'dashboard' ? (
          <DashboardTab
            snap={snap}
            all={all}
            pinfo={pinfo}
            tr={tr}
            lang={lang}
            onClockOut={(empId, at) => run(() => api.clockOutEmployee(empId, at))}
          />
        ) : null}
        {nav === 'analytics' ? <AnalyticsTab snap={snap} periodCalcs={all} pinfo={pinfo} tr={tr} lang={lang} /> : null}
        {nav === 'employees' ? (
          <EmployeesTab
            snap={snap}
            all={all}
            tr={tr}
            lang={lang}
            actions={{
              create: (input) => run(() => api.createEmployee(input)),
              update: (id, patch) => run(() => api.updateEmployee(id, patch)),
              deactivate: (id) => run(() => api.deactivateEmployee(id)),
              reactivate: (id) => run(() => api.reactivateEmployee(id)),
              remove: (id) => run(() => api.deleteEmployee(id)),
              setGrant: (id, grant) => run(() => api.setEditGrant(id, grant)),
            }}
          />
        ) : null}
        {nav === 'settings' ? (
          <SettingsTab snap={snap} pinfo={pinfo} tr={tr} lang={lang} onChange={(patch) => run(() => api.updateSettings(patch))} />
        ) : null}
        {nav === 'approvals' ? (
          <ApprovalsTab
            snap={snap}
            tr={tr}
            lang={lang}
            onRequest={(id, status) => run(() => api.decideRequest(id, status))}
            onTimesheet={(id, status) => run(() => api.decideTimesheet(id, status))}
          />
        ) : null}
        {nav === 'importer' ? (
          <ImporterTab
            snap={snap}
            tr={tr}
            lang={lang}
            actions={{
              importCsv: async (csv) => {
                const { result, snapshot } = await api.importWorklogs(csv);
                await res.mutate(async () => snapshot);
                return result;
              },
              addAdjustment: (input) => run(() => api.createAdjustment(input)),
              deleteAdjustment: (id) => run(() => api.deleteAdjustment(id)),
              correctHours: async (input) => {
                const { oldH, newH, snapshot } = await api.correctHours(input);
                await res.mutate(async () => snapshot);
                return { oldH, newH };
              },
              deleteCorrection: (id) => run(() => api.deleteHourCorrection(id)),
            }}
          />
        ) : null}
        {nav === 'reports' ? <ReportsTab all={all} pinfo={pinfo} tr={tr} /> : null}
        {nav === 'timesheets' ? <TimesheetsTab snap={snap} pinfo={pinfo} tr={tr} lang={lang} /> : null}
        {nav === 'payroll' ? <PayrollTab snap={snap} pinfo={pinfo} tr={tr} lang={lang} /> : null}
      </div>
    </div>
  );
}

export type { Tr };
